/*
 * Standard product page (templates/product.standard.json).
 * Cart submission is handled by the theme's <product-form> (assets/product-form.js);
 * this file only manages option selection, the gallery, the pairing carousel,
 * the size chart dialog and lazy-loaded recommendations.
 */

if (!customElements.get('pdp-product')) {
  customElements.define('pdp-product', class PdpProduct extends HTMLElement {
    connectedCallback() {
      this.variants = this.readJSON('[data-pdp-variants]') || [];
      this.strings = this.readJSON('[data-pdp-strings]') || {};
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.submitButton = this.querySelector('[data-pdp-submit]');
      this.submitLabel = this.querySelector('[data-pdp-submit-label]');
      this.priceEl = this.querySelector('[data-pdp-price]');
      this.errorEl = this.querySelector('[data-pdp-option-error]');
      this.fieldsets = [...this.querySelectorAll('.pdp-option')];
      this.productForm = this.querySelector('product-form');

      // Option values are links to their variant URL (the no-JS fallback);
      // with this script they select in place instead of reloading.
      this.addEventListener('click', (event) => {
        const link = event.target.closest('.pdp-option__label');
        if (!link || !this.contains(link)) return;
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        const grid = link.closest('.pdp-option__grid');
        grid.querySelectorAll('.pdp-option__label').forEach((el) => el.removeAttribute('aria-current'));
        link.setAttribute('aria-current', 'true');
        this.onOptionChange();
      });

      // Runs before <product-form> handles submit: block adding when no option is chosen.
      if (this.productForm) {
        this.productForm.addEventListener('submit', (event) => {
          if (this.variantInput && this.variantInput.value) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showOptionError();
        }, true);
      }
    }

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const selected = fieldset.querySelector('.pdp-option__label[aria-current="true"]');
        return selected ? selected.dataset.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();

      this.fieldsets.forEach((fieldset, index) => {
        const valueEl = fieldset.querySelector('[data-selected-value]');
        if (valueEl) valueEl.textContent = selected[index] || '';
      });

      this.updateAvailability(selected);

      if (selected.includes(null)) {
        this.setVariant(null);
        return;
      }

      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
      this.setVariant(variant || null, true);
    }

    // For multi-option products, grey out values that have no available variant
    // given the other current selections. Single-option products are already
    // correct from Liquid, and this keeps them unchanged.
    updateAvailability(selected) {
      if (this.fieldsets.length < 2) return;
      this.fieldsets.forEach((fieldset, index) => {
        fieldset.querySelectorAll('.pdp-option__label').forEach((link) => {
          const available = this.variants.some((v) => v.available
            && v.options[index] === link.dataset.value
            && v.options.every((value, i) => i === index || selected[i] === null || value === selected[i]));
          const isSelected = link.getAttribute('aria-current') === 'true';
          const unavailable = !available && !isSelected;
          if (unavailable) {
            link.setAttribute('aria-disabled', 'true');
            link.setAttribute('tabindex', '-1');
          } else {
            link.removeAttribute('aria-disabled');
            link.removeAttribute('tabindex');
          }
          const soldOut = link.querySelector('[data-pdp-sold-out]');
          if (soldOut) soldOut.hidden = available;
        });
      });
    }

    setVariant(variant, updateUrl) {
      if (this.errorEl) this.errorEl.hidden = true;

      this.variantInput.value = variant ? variant.id : '';

      if (!this.submitButton) return;
      if (variant && !variant.available) {
        this.submitButton.disabled = true;
        this.submitLabel.textContent = this.strings.soldOut;
      } else {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitLabel.textContent = this.strings.cta;
      }

      if (!variant) return;

      document.dispatchEvent(new CustomEvent('afterVariantChanged', {
        detail: {
          sectionId: this.dataset.sectionId,
          variant,
        },
      }));

      if (this.priceEl && variant.price_html) this.priceEl.innerHTML = variant.price_html;

      if (variant.featured_media_id) {
        const gallery = this.querySelector('pdp-gallery');
        if (gallery) gallery.show(String(variant.featured_media_id));
      }

      const pickup = this.querySelector('pickup-availability');
      if (pickup && typeof pickup.update === 'function') pickup.update(variant);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    showOptionError() {
      if (this.errorEl) this.errorEl.hidden = false;
      const firstEmpty = this.fieldsets.find((fieldset) => !fieldset.querySelector('.pdp-option__label[aria-current="true"]'));
      const target = firstEmpty && firstEmpty.querySelector('.pdp-option__label:not([aria-disabled="true"])');
      if (target) {
        firstEmpty.scrollIntoView({ block: 'center', behavior: 'smooth' });
        target.focus({ preventScroll: true });
      }
    }
  });
}

if (!customElements.get('pdp-gallery')) {
  customElements.define('pdp-gallery', class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('.pdp-gallery__thumb');
        if (thumb) this.show(thumb.dataset.mediaId);
      });
    }

    show(mediaId) {
      const slide = this.querySelector(`.pdp-gallery__slide[data-media-id="${mediaId}"]`);
      if (!slide) return;

      this.querySelectorAll('.pdp-gallery__slide').forEach((el) => {
        if (el === slide) {
          el.hidden = false;
        } else if (!el.hidden) {
          el.hidden = true;
          el.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      this.querySelectorAll('.pdp-gallery__thumb').forEach((el) => {
        el.setAttribute('aria-current', el.dataset.mediaId === mediaId ? 'true' : 'false');
      });
    }
  });
}

if (!customElements.get('pdp-carousel')) {
  customElements.define('pdp-carousel', class PdpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-track]');
      this.dots = [...this.querySelectorAll('[data-pdp-dot]')];
      if (!this.track || this.dots.length === 0) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      let frame = null;
      this.track.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          this.setCurrent(this.currentIndex());
        });
      }, { passive: true });
    }

    step() {
      const first = this.track.children[0];
      if (!first) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    currentIndex() {
      return Math.round(this.track.scrollLeft / this.step());
    }

    goTo(index) {
      this.track.scrollTo({ left: index * this.step(), behavior: 'smooth' });
      this.setCurrent(index);
    }

    setCurrent(index) {
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
  });
}

if (!customElements.get('pdp-recommendations')) {
  customElements.define('pdp-recommendations', class PdpRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const fresh = html.querySelector('pdp-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
            } else {
              this.closest('.shopify-section')?.setAttribute('hidden', '');
            }
          })
          .catch((e) => console.error(e));
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
  });
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-pdp-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.pdpDialogOpen);
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    return;
  }

  const closer = event.target.closest('[data-pdp-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop closes the dialog.
  if (event.target.classList && event.target.classList.contains('pdp-dialog')) {
    event.target.close();
  }
});
